// Intent (1.2.0): phone-on-demand + restore 2FA. The phone is NOT collected at
// signup anymore — only where it earns its keep: (a) restoring cloud data on a
// new device (double lock on financial data — server enforces it on the sync
// pull), (b) attaching a number for CRM linking / referral voucher / household.
// mode=restore → code goes to the phone already on the account (no input);
// otherwise the user types a new number first. Stitch prompt: "add-phone".

import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, fonts, radius, spacing } from '@/theme/tokens';

const RESEND_COOLDOWN_S = 30;

export default function AuthAddPhoneScreen() {
  const { t } = useTranslation();
  const { mode } = useLocalSearchParams<{ mode?: string }>();
  const account = useAuthStore((s) => s.account);
  const requestPhoneCode = useAuthStore((s) => s.requestPhoneCode);
  const verifyPhoneCode = useAuthStore((s) => s.verifyPhoneCode);

  const restore = mode === 'restore';
  // Restore = confirm the EXISTING phone; attach = type a new one.
  const hasPhone = Boolean(account?.phone);

  const [digits, setDigits] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1_000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const phoneParam = hasPhone ? undefined : `+48${digits}`;
  const formatted = digits.replace(/(\d{3})(?=\d)/g, '$1 ');

  const onSend = async () => {
    setBusy(true);
    setError(null);
    const result = await requestPhoneCode(phoneParam);
    setBusy(false);
    if (result.ok) {
      setCodeSent(true);
      setResendIn(RESEND_COOLDOWN_S);
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  const onVerify = async () => {
    setBusy(true);
    setError(null);
    const result = await verifyPhoneCode(code.trim(), phoneParam);
    setBusy(false);
    if (result.ok) {
      // Fresh SMS-verified tokens are in — the cloud pull is unlocked now.
      router.dismissTo('/menu' as never);
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  const maskedPhone = account?.phone
    ? account.phone.replace(/^(\+48)(\d{3})\d{4}(\d{2})$/, '$1 $2 *** *$3')
    : '';

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        keyboardShouldPersistTaps="handled"
      >
        <ScreenHeader title={restore ? t('auth.restoreTitle') : t('auth.addPhoneTitle')} />

        <View style={{ alignItems: 'center', marginTop: spacing.md }}>
          <View
            style={{
              width: 88,
              height: 88,
              borderRadius: 44,
              backgroundColor: colors.primaryTile,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="shield-checkmark-outline" size={40} color={colors.primary} />
          </View>
        </View>

        <AppText variant="headlineLg" color={colors.navy} style={{ textAlign: 'center' }}>
          {restore ? t('auth.restoreHeadline') : t('auth.addPhoneHeadline')}
        </AppText>
        <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
          {restore
            ? hasPhone
              ? t('auth.restoreBody', { phone: maskedPhone })
              : t('auth.restoreNoPhoneBody')
            : t('auth.addPhoneBody')}
        </AppText>

        {!hasPhone && (
          <View style={{ gap: spacing.base }}>
            <AppText variant="labelMd" color={colors.primary}>
              {t('auth.phoneLabel')}
            </AppText>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: colors.primaryTile,
                borderRadius: radius.card,
                paddingHorizontal: spacing.md,
                minHeight: 64,
                gap: spacing.sm,
              }}
            >
              <AppText variant="headlineSm" color={colors.navy}>
                +48
              </AppText>
              <TextInput
                value={formatted}
                onChangeText={(text) => setDigits(text.replace(/\D/g, '').slice(0, 9))}
                placeholder="___ ___ ___"
                placeholderTextColor={colors.outlineVariant}
                keyboardType="phone-pad"
                editable={!codeSent}
                style={{
                  flex: 1,
                  fontFamily: fonts.semiBold,
                  fontSize: 22,
                  color: colors.navy,
                  paddingVertical: spacing.sm,
                }}
              />
              <Ionicons name="call-outline" size={22} color={colors.primary} />
            </View>
          </View>
        )}

        {codeSent && (
          <View style={{ gap: spacing.base }}>
            <AppText variant="labelMd" color={colors.primary}>
              {t('auth.addPhoneCodeLabel')}
            </AppText>
            <TextInput
              value={code}
              onChangeText={(text) => setCode(text.replace(/\D/g, '').slice(0, 6))}
              placeholder="______"
              placeholderTextColor={colors.outlineVariant}
              keyboardType="number-pad"
              autoFocus
              style={{
                backgroundColor: colors.primaryTile,
                borderRadius: radius.card,
                paddingHorizontal: spacing.md,
                minHeight: 56,
                fontFamily: fonts.semiBold,
                fontSize: 24,
                letterSpacing: 8,
                color: colors.navy,
                textAlign: 'center',
              }}
            />
          </View>
        )}

        {error && (
          <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
            {error}
          </AppText>
        )}

        {codeSent ? (
          <>
            <PrimaryButton
              label={t('auth.addPhoneVerify')}
              disabled={code.length !== 6 || busy}
              onPress={onVerify}
            />
            <Pressable
              accessibilityRole="button"
              onPress={onSend}
              disabled={resendIn > 0 || busy}
              style={{ minHeight: 44, justifyContent: 'center', opacity: resendIn > 0 ? 0.5 : 1 }}
            >
              <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
                {resendIn > 0 ? t('auth.emailResendIn', { s: resendIn }) : t('auth.addPhoneResend')}
              </AppText>
            </Pressable>
          </>
        ) : (
          <PrimaryButton
            label={t('auth.addPhoneSend')}
            disabled={busy || (!hasPhone && digits.length !== 9)}
            onPress={onSend}
          />
        )}

        <Pressable
          accessibilityRole="button"
          onPress={() => router.dismissTo('/menu' as never)}
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.addPhoneLater')}
          </AppText>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
